import { useEffect } from 'react';
import { getValidationResolver, useForm } from '@common-fe/common-fe';

import { VALIDATORS } from '@/common';
import { PayoutDefinition } from '@/modules/ProcessingDefinition/ProcessingDefinition.types';
import { CategoryTypeDto } from '@/modules/ProcessingDefinition/Services/Services.types';

import { PayoutDefinitionField, PayoutDefinitionFormValues } from '../PayoutDefinitionSetup.types';
import { DEFAULT_COV_TABLE_KEY } from '../widgets/BasicProperties/queries';
import {
  DEFAULT_COLUMNS_COUNT,
  MIN_COLUMNS_COUNT,
} from '../widgets/PayoutOrder/components/PayoutOrder.constants';
import { buildServiceRowId } from '../widgets/PayoutOrder/hooks/payoutOrderRowId.utils';

interface UsePayoutDefinitionSetupFormParams {
  data?: PayoutDefinition;
}

const mapDataToFormValues = (
  data: PayoutDefinition | undefined,
  canBeLimited: boolean
): PayoutDefinitionFormValues => {
  const categories = (data?.categoryTypes || []).map((category) => ({
    categoryType: (category.categoryType || '') as CategoryTypeDto,
    services: (category.services || []).map((service) => ({
      rowId: buildServiceRowId(service.id, service.sequenceId),
      id: service.id,
      name: service.name,
      serviceType: service.serviceType || '',
      sequenceId: service.sequenceId,
      plans: (service.plans || []).map((plan) => ({
        id: plan.id,
        name: plan.name,
        accountType: plan.accountType,
        order: plan.order,
      })),
    })),
  }));

  const maxOrder = categories.reduce(
    (max, category) =>
      category.services.reduce(
        (serviceMax, service) =>
          service.plans.reduce((planMax, plan) => Math.max(planMax, plan.order), serviceMax),
        max
      ),
    0
  );

  const hasAnyPlans = categories.some((c) => c.services.some((s) => s.plans.length > 0));

  return {
    [PayoutDefinitionField.payoutDefinitionName]: data?.name || '',
    [PayoutDefinitionField.mpTableId]: data?.mpTableId || '',
    [PayoutDefinitionField.covTableId]: data?.covTableId || DEFAULT_COV_TABLE_KEY,
    [PayoutDefinitionField.isLimited]: Boolean(canBeLimited && data?.isLimited),
    [PayoutDefinitionField.categories]: categories,
    [PayoutDefinitionField.columnsCount]: hasAnyPlans
      ? Math.max(MIN_COLUMNS_COUNT, maxOrder)
      : DEFAULT_COLUMNS_COUNT,
  };
};

const usePayoutDefinitionSetupForm = ({ data }: UsePayoutDefinitionSetupFormParams) => {
  const canBeLimited = !!data?.canBeLimited;

  const methods = useForm<PayoutDefinitionFormValues>({
    defaultValues: mapDataToFormValues(data, canBeLimited),
    resolver: getValidationResolver({
      [PayoutDefinitionField.payoutDefinitionName]: VALIDATORS.REQUIRED_STRING,
      [PayoutDefinitionField.mpTableId]: VALIDATORS.REQUIRED_STRING,
    }),
  });

  const { reset } = methods;

  useEffect(() => {
    if (data) {
      reset(mapDataToFormValues(data, canBeLimited));
    }
  }, [data, reset, canBeLimited]);

  return { methods, canBeLimited };
};

export default usePayoutDefinitionSetupForm;
